import { useCallback, useEffect, useMemo, useState } from 'react';
import { CaretDown, Funnel, MagnifyingGlass, Tag } from 'shared/icons';
import api from '../../../services/api';
import { CountLabel } from '../../../utils/format';
import { MLB_URLS } from '../../../utils/ml';
import {
    FmtDate,
    FmtInt,
    FmtMoney,
    IsProductActive,
    LegacyEmptyState,
    LegacyFilterPills,
    LegacyLoading,
    LegacySearchBox,
    LegacySection,
    LegacyStatCard,
    LegacyStatusPill,
    TYPE_COLORS,
    TYPE_ICONS,
    Unwrap,
} from './legacyShared';

/**
 * Aba Legacy "Por Campanha" — cópia de campaigns-v2.html (tab by-campaign) do
 * Campaign Manager. Mesma disposição: 6 stats → busca → pills → filtros
 * avançados → contagem → cards de campanha com 6 seções e itens expansíveis.
 */
const LegacyByCampaign = ({ includeInactive }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [search, setSearch] = useState('');
    const [pillFilter, setPillFilter] = useState('all');
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [filterStatus, setFilterStatus] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterSubType, setFilterSubType] = useState('');
    const [filterSort, setFilterSort] = useState('finish_asc');
    const [expanded, setExpanded] = useState({}); // campaignId -> true
    const [itemsCache, setItemsCache] = useState({}); // campaignId -> rows | 'loading' | {error}

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const payload = Unwrap(await api.get('/legacy-campaigns/campaigns'));
            setCampaigns(Array.isArray(payload?.campaigns) ? payload.campaigns : []);
        } catch (err) {
            setError(err.message || 'Erro ao carregar campanhas');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const types = useMemo(() => [...new Set(campaigns.map((c) => c.type).filter(Boolean))].sort(), [campaigns]);
    const subTypes = useMemo(
        () => [...new Set(campaigns.map((c) => c.sub_type).filter(Boolean))].sort(),
        [campaigns],
    );

    const now = Date.now();
    const in7d = now + 7 * 86400000;
    const hasRebate = (c) =>
        parseFloat(c.fixed_percentage || 0) > 0 || ['PRE_NEGOTIATED', 'SMART', 'PRICE_MATCHING'].includes(c.type);
    const endingSoon = (c) =>
        c.finish_date && new Date(c.finish_date).getTime() >= now && new Date(c.finish_date).getTime() <= in7d;

    const pills = useMemo(
        () => [
            { id: 'all', label: 'Todas', count: campaigns.length },
            { id: 'started', label: 'Ativas', count: campaigns.filter((c) => c.status === 'started').length },
            { id: 'pending', label: 'Pendentes', count: campaigns.filter((c) => c.status === 'pending').length },
            { id: 'with-rebate', label: 'Com Rebate', count: campaigns.filter(hasRebate).length },
            {
                id: 'with-items',
                label: 'Com Itens',
                count: campaigns.filter((c) => parseInt(c.total_items || 0, 10) > 0).length,
            },
            { id: 'ending-soon', label: 'Acabando', count: campaigns.filter(endingSoon).length },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [campaigns],
    );

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const rows = campaigns.filter((c) => {
            if (q) {
                const hay = `${c.name || ''} ${c.id || ''} ${c.type || ''} ${c.sub_type || ''}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            if (filterStatus && c.status !== filterStatus) return false;
            if (filterType && c.type !== filterType) return false;
            if (filterSubType && c.sub_type !== filterSubType) return false;
            if (pillFilter === 'started' && c.status !== 'started') return false;
            if (pillFilter === 'pending' && c.status !== 'pending') return false;
            if (pillFilter === 'with-rebate' && !hasRebate(c)) return false;
            if (pillFilter === 'with-items' && parseInt(c.total_items || 0, 10) === 0) return false;
            if (pillFilter === 'ending-soon' && !endingSoon(c)) return false;
            return true;
        });
        rows.sort((a, b) => {
            if (filterSort === 'items_desc') return parseInt(b.total_items || 0, 10) - parseInt(a.total_items || 0, 10);
            if (filterSort === 'participating_desc')
                return parseInt(b.items_started || 0, 10) - parseInt(a.items_started || 0, 10);
            if (filterSort === 'name_asc') return (a.name || '').localeCompare(b.name || '');
            const af = a.finish_date ? new Date(a.finish_date).getTime() : Infinity;
            const bf = b.finish_date ? new Date(b.finish_date).getTime() : Infinity;
            return af - bf;
        });
        return rows;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaigns, search, pillFilter, filterStatus, filterType, filterSubType, filterSort]);

    const toggleCard = async (campId) => {
        const isOpen = expanded[campId];
        setExpanded((prev) => ({ ...prev, [campId]: !isOpen }));
        if (isOpen || itemsCache[campId]) return;
        setItemsCache((prev) => ({ ...prev, [campId]: 'loading' }));
        try {
            const items = Unwrap(await api.get(`/legacy-campaigns/campaigns/${encodeURIComponent(campId)}/items`));
            setItemsCache((prev) => ({ ...prev, [campId]: Array.isArray(items) ? items : [] }));
        } catch (err) {
            setItemsCache((prev) => ({ ...prev, [campId]: { error: err.message } }));
        }
    };

    if (loading) return <LegacyLoading text="Carregando campanhas..." />;
    if (error)
        return (
            <LegacyEmptyState
                icon={MagnifyingGlass}
                text={`Erro: ${error} — verifique a fonte legada (LEGACY_PG_HOST).`}
            />
        );

    return (
        <div className="space-y-4">
            {/* Stats (6 cards, como no legado) */}
            <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3 lg:grid-cols-6">
                <LegacyStatCard label="Total Campanhas" value={FmtInt(campaigns.length)} />
                <LegacyStatCard label="Ativas"
                    value={FmtInt(campaigns.filter((c) => c.status === 'started').length)}
                />
                <LegacyStatCard label="Pendentes"
                    value={FmtInt(campaigns.filter((c) => c.status === 'pending').length)}
                />
                <LegacyStatCard label="Total Itens"
                    value={FmtInt(campaigns.reduce((s, c) => s + parseInt(c.total_items || 0, 10), 0))}
                />
                <LegacyStatCard label="Itens Candidatos"
                    value={FmtInt(campaigns.reduce((s, c) => s + parseInt(c.items_candidate || 0, 10), 0))}
                />
                <LegacyStatCard label="Tipos Distintos" value={new Set(campaigns.map((c) => c.type)).size} />
            </div>

            {/* Busca + pills + filtros avançados */}
            <div className="space-y-3">
                <div className="flex items-center gap-2">
                    <div className="flex-1">
                        <LegacySearchBox
                            value={search}
                            onChange={setSearch}
                            placeholder="Buscar nome ou ID da campanha..."
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowAdvanced((v) => !v)}
                        className="flex items-center gap-1.5 rounded-lg border border-mono-200 bg-white px-3 py-2.5 text-sm text-mono-600 hover:border-mono-400 dark:border-mono-800 dark:bg-mono-950 dark:text-mono-400"
                    >
                        <Funnel size={16} /> Filtros
                    </button>
                </div>
                <LegacyFilterPills pills={pills} active={pillFilter} onSelect={setPillFilter} />
                {showAdvanced && (
                    <div className="rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
                        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                            {[
                                {
                                    label: 'Status',
                                    value: filterStatus,
                                    set: setFilterStatus,
                                    options: ['started', 'pending', 'finished', 'paused'],
                                },
                                { label: 'Tipo', value: filterType, set: setFilterType, options: types },
                                { label: 'Sub-tipo', value: filterSubType, set: setFilterSubType, options: subTypes },
                            ].map((f) => (
                                <div key={f.label}>
                                    <label className="mb-1 block text-[10px] uppercase text-mono-400">{f.label}</label>
                                    <select
                                        value={f.value}
                                        onChange={(e) => f.set(e.target.value)}
                                        className="w-full rounded-lg border border-mono-200 bg-white px-2.5 py-2 text-[13px] text-black dark:border-mono-800 dark:bg-mono-950 dark:text-white"
                                    >
                                        <option value="">Todos</option>
                                        {f.options.map((o) => (
                                            <option key={o} value={o}>
                                                {o}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            ))}
                            <div>
                                <label className="mb-1 block text-[10px] uppercase text-mono-400">Ordenar por</label>
                                <select
                                    value={filterSort}
                                    onChange={(e) => setFilterSort(e.target.value)}
                                    className="w-full rounded-lg border border-mono-200 bg-white px-2.5 py-2 text-[13px] text-black dark:border-mono-800 dark:bg-mono-950 dark:text-white"
                                >
                                    <option value="finish_asc">Vigência (próxima ao fim)</option>
                                    <option value="items_desc">Mais itens</option>
                                    <option value="participating_desc">Mais participando</option>
                                    <option value="name_asc">Nome (A-Z)</option>
                                </select>
                            </div>
                        </div>
                        <div className="mt-3 border-t border-mono-100 pt-3 dark:border-mono-900">
                            <button
                                type="button"
                                onClick={() => {
                                    setFilterStatus('');
                                    setFilterType('');
                                    setFilterSubType('');
                                    setFilterSort('finish_asc');
                                    setSearch('');
                                    setPillFilter('all');
                                }}
                                className="rounded-lg border border-mono-200 px-4 py-2 text-[13px] text-mono-600 dark:border-mono-800 dark:text-mono-400"
                            >
                                Limpar
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Contagem */}
            <p className="text-sm text-mono-500">
                <strong className="text-black dark:text-white">{FmtInt(filtered.length)}</strong> de{' '}
                {FmtInt(campaigns.length)} campanhas
            </p>

            {/* Cards */}
            {filtered.length === 0 ? (
                <LegacyEmptyState icon={MagnifyingGlass} text="Nenhuma campanha encontrada" />
            ) : (
                <div className="flex flex-col gap-2.5">
                    {filtered.map((c) => {
                        const total = parseInt(c.total_items || 0, 10);
                        const started = parseInt(c.items_started || 0, 10);
                        const cand = parseInt(c.items_candidate || 0, 10);
                        const pending = parseInt(c.items_pending || 0, 10);
                        const participation = total > 0 ? Math.round((started / total) * 100) : 0;
                        const Icon = TYPE_ICONS[c.type] || Tag;
                        const isOpen = expanded[c.id];
                        const cached = itemsCache[c.id];
                        return (
                            <div
                                key={c.id}
                                className="overflow-hidden rounded-xl border border-mono-200 bg-white transition-colors hover:border-mono-400 dark:border-mono-800 dark:bg-mono-950 dark:hover:border-mono-600"
                            >
                                {/* topo */}
                                <button
                                    type="button"
                                    onClick={() => toggleCard(c.id)}
                                    className="flex w-full items-center gap-3 border-b border-mono-100 p-3 text-left dark:border-mono-900"
                                >
                                    <div
                                        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border-2 bg-mono-50 dark:bg-mono-900 ${TYPE_COLORS[c.type] || 'text-mono-400 border-mono-300 dark:border-mono-700'}`}
                                    >
                                        <Icon size={22} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-semibold text-black dark:text-white">
                                            {c.name || c.id}
                                        </p>
                                        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px]">
                                            <span className="font-mono text-mono-400">{c.id}</span>
                                            <span className="rounded-md border border-mono-200 bg-mono-50 px-1.5 py-px text-[10px] font-semibold text-mono-500 dark:border-mono-800 dark:bg-mono-900">
                                                {c.type || '-'}
                                            </span>
                                            {c.sub_type ? <span className="text-[10px] text-mono-400">· {c.sub_type}</span> : null}
                                            <LegacyStatusPill status={c.status} />
                                        </div>
                                    </div>
                                    <CaretDown
                                        size={18}
                                        className={`shrink-0 text-mono-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                                    />
                                </button>
                                {/* seções */}
                                <div className="grid grid-cols-3 md:grid-cols-6">
                                    <LegacySection
                                        label="Vigência"
                                        sub={c.deadline_date ? `limite: ${FmtDate(c.deadline_date)}` : null}
                                    >
                                        <span className="text-[11px] font-semibold">
                                            {FmtDate(c.start_date)}
                                            <span className="mx-1 text-mono-400">→</span>
                                            {FmtDate(c.finish_date)}
                                        </span>
                                    </LegacySection>
                                    <LegacySection label="Itens">{FmtInt(total)}</LegacySection>
                                    <LegacySection label="Participando">
                                        <span className="text-green-600 dark:text-green-400">{FmtInt(started)}</span>
                                        <div className="mt-1 h-1 overflow-hidden rounded-full bg-mono-100 dark:bg-mono-900">
                                            <div
                                                className="h-full rounded-full bg-green-500"
                                                style={{ width: `${participation}%` }}
                                            />
                                        </div>
                                    </LegacySection>
                                    <LegacySection label="Candidatos" sub={pending > 0 ? `${pending} pending` : null}>
                                        <span className="text-orange-500">{FmtInt(cand)}</span>
                                    </LegacySection>
                                    <LegacySection label="Adesão">
                                        <span
                                            className={
                                                participation >= 50
                                                    ? 'text-green-600 dark:text-green-400'
                                                    : participation >= 20
                                                      ? 'text-orange-500'
                                                      : 'text-red-500'
                                            }
                                        >
                                            {participation}%
                                        </span>
                                    </LegacySection>
                                    <LegacySection label="Rebate ML">
                                        {parseFloat(c.fixed_percentage || 0) > 0 ? (
                                            <span className="text-orange-500">
                                                {parseFloat(c.fixed_percentage).toFixed(1)}%
                                            </span>
                                        ) : ['PRE_NEGOTIATED', 'SMART', 'PRICE_MATCHING'].includes(c.type) ? (
                                            <span className="text-blue-500">por item</span>
                                        ) : (
                                            <span className="text-mono-400">—</span>
                                        )}
                                    </LegacySection>
                                </div>
                                {/* itens expansíveis */}
                                {isOpen && (
                                    <div className="border-t border-mono-100 bg-mono-50 p-3 dark:border-mono-900 dark:bg-mono-900/40">
                                        <LegacyCampaignItems cached={cached} includeInactive={includeInactive} />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// Tabela de itens da campanha expandida — colunas do legado:
// Item | Status | Consumer | Líquido | Rebate | Meli%
const LegacyCampaignItems = ({ cached, includeInactive }) => {
    if (!cached || cached === 'loading')
        return <p className="py-3 text-center text-xs text-mono-500">Carregando itens...</p>;
    if (cached.error) return <p className="py-3 text-center text-xs text-red-500">Erro: {cached.error}</p>;
    if (cached.length === 0) return <p className="py-3 text-center text-xs text-mono-500">Sem itens nesta campanha</p>;

    const filteredAll = includeInactive ? cached : cached.filter((i) => IsProductActive(i.product_status));
    if (filteredAll.length === 0)
        return (
            <p className="py-3 text-center text-xs text-mono-500">
                Sem itens ativos nesta campanha ({cached.length} inativos ocultos)
            </p>
        );
    const top = filteredAll.slice(0, 20);

    return (
        <div className="overflow-x-auto">
            <div className="min-w-[640px]">
                <div className="grid grid-cols-[1.5fr_0.7fr_0.7fr_0.7fr_0.7fr_0.5fr] gap-2 border-b border-mono-200 px-1 py-1 text-[9px] font-semibold uppercase text-mono-400 dark:border-mono-800">
                    <span>Item</span>
                    <span className="text-right">Status</span>
                    <span className="text-right">Consumer</span>
                    <span className="text-right">Líquido</span>
                    <span className="text-right">Rebate</span>
                    <span className="text-right">Meli%</span>
                </div>
                {top.map((i) => {
                    // fórmula v2 do legado: rebate = original × meli% / 100; líquido = consumer + rebate
                    const promoPrice = parseFloat(i.price || i.suggested_discounted_price || 0);
                    const meliPct = parseFloat(i.meli_percentage || 0);
                    const original = parseFloat(i.original_price || 0);
                    const rebate = meliPct > 0 && original > 0 ? (original * meliPct) / 100 : null;
                    const net = promoPrice > 0 ? promoPrice + (rebate || 0) : null;
                    return (
                        <div
                            key={`${i.product_id}-${i.campaign_id}`}
                            className={`grid grid-cols-[1.5fr_0.7fr_0.7fr_0.7fr_0.7fr_0.5fr] items-center gap-2 border-b border-mono-100 px-1 py-1.5 text-[11px] tabular-nums last:border-b-0 dark:border-mono-900 ${i.is_active_campaign ? 'bg-orange-50 dark:bg-orange-900/10' : ''}`}
                        >
                            <div className="min-w-0">
                                <p className="flex items-center gap-1 truncate">
                                    {i.product_thumbnail ? (
                                        <img
                                            src={i.product_thumbnail}
                                            alt=""
                                            className="h-5 w-5 rounded-md border border-mono-200 object-cover dark:border-mono-800"
                                        />
                                    ) : null}
                                    <a
                                        href={MLB_URLS.search(i.product_id)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="font-mono text-red-600 hover:underline dark:text-red-400"
                                    >
                                        {i.product_id}
                                    </a>
                                    {i.is_active_campaign ? (
                                        <span className="rounded-md bg-orange-500 px-1 py-px text-[9px] font-bold text-black">
                                            ATIVA
                                        </span>
                                    ) : null}
                                </p>
                                <p className="truncate text-[10px] text-mono-400">
                                    {i.product_sku || '—'} · {(i.product_title || '').slice(0, 40)}
                                </p>
                            </div>
                            <span className="text-right">{i.status || '-'}</span>
                            <span className="text-right">{FmtMoney(promoPrice || null)}</span>
                            <span className="text-right font-semibold text-green-600 dark:text-green-400">
                                {FmtMoney(net)}
                            </span>
                            <span className="text-right text-orange-500">{rebate != null ? FmtMoney(rebate) : '—'}</span>
                            <span className="text-right">{meliPct > 0 ? `${meliPct.toFixed(1)}%` : '—'}</span>
                        </div>
                    );
                })}
                <p className="pt-1.5 text-[11px] text-mono-500">
                    {filteredAll.length > 20
                        ? `Mostrando ${top.length} de ${filteredAll.length}`
                        : CountLabel(filteredAll.length, 'item', 'itens')}
                    {!includeInactive && cached.length > filteredAll.length
                        ? ` (${cached.length - filteredAll.length} inativos ocultos)`
                        : ''}
                </p>
            </div>
        </div>
    );
};

export default LegacyByCampaign;
